import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, AfterViewChecked, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

interface ChatSession {
  id: string;
  title: string;
  timestamp: Date;
}

@Component({
  selector: 'tidy-dashboard-chat-sidebar',
  templateUrl: './dashboard-chat-sidebar.component.html',
  styleUrls: ['./dashboard-chat-sidebar.component.scss'],
  standalone: false
})
export class DashboardChatSidebarComponent implements OnInit, AfterViewChecked, OnChanges {
  @Input() propertySummary = '';
  @Output() messageSent = new EventEmitter<string>();
  @ViewChild('messageInput') messageInput: ElementRef;

  chatForm: UntypedFormGroup;
  isRespondingFromLLM = false;
  isTyping = false;
  chatMessages: string[] = [];
  chatSessions: ChatSession[] = [];
  currentSessionId = '';
  currentSessionName = '';
  showSessions = false;

  constructor(private fb: UntypedFormBuilder, private cdr: ChangeDetectorRef) {
    this.chatForm = this.fb.group({
      message: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.initializeDefaultMessage();
    this.loadSessions();
    this.startNewSession();
  }

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['propertySummary'] && this.propertySummary) {
      this.chatMessages = [this.propertySummary];
    }
  }

  private initializeDefaultMessage(): void {
    const defaultSummary = 'Good morning! Here\'s your daily summary: You have 5 jobs scheduled today, 2 unread messages, and $1,240 in pending invoices. Everything is running smoothly!';
    this.chatMessages = [defaultSummary];
  }

  sendMessage(): void {
    if (this.chatForm.valid && !this.isRespondingFromLLM) {
      const message = this.chatForm.get('message').value.trim();
      if (message) {
        this.messageSent.emit(message);
        this.chatForm.reset();
        this.saveCurrentSession();
        this.showLLMResponse();
      }
    }
  }

  private saveCurrentSession(): void {
    const sessionExists = this.chatSessions.find(s => s.id === this.currentSessionId);

    if (!sessionExists && this.chatMessages.length > 0) {
      const firstMessage = this.chatMessages[0];
      const title = firstMessage.substring(0, 30) + (firstMessage.length > 30 ? '...' : '');
      const newSession: ChatSession = {
        id: this.currentSessionId,
        title,
        timestamp: new Date()
      };
      this.chatSessions.unshift(newSession);
      this.saveSessions();
    }

    localStorage.setItem(`chatSession-${this.currentSessionId}`, JSON.stringify(this.chatMessages));
  }

  private showLLMResponse(): void {
    this.isRespondingFromLLM = true;
    this.isTyping = true;

    const sampleResponse = 'I can help you manage your dashboard. You can ask me about your schedules, messages, invoices, or any other information you need.';
    let currentIndex = 0;
    const messageIndex = this.chatMessages.length;
    this.chatMessages.push('');

    const typingInterval = setInterval(() => {
      if (currentIndex < sampleResponse.length) {
        this.chatMessages[messageIndex] += sampleResponse[currentIndex];
        this.cdr.markForCheck();
        currentIndex++;
      } else {
        this.isTyping = false;
        this.isRespondingFromLLM = false;
        this.saveCurrentSession();
        this.cdr.markForCheck();
        clearInterval(typingInterval);
      }
    }, 30);
  }

  private scrollToBottom(): void {
    try {
      const container = document.querySelector('.sidebar-chat-messages');
      if (container) {
        setTimeout(() => {
          container.scrollTop = container.scrollHeight;
        }, 0);
      }
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  onMessageInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  startNewSession(): void {
    const sessionId = `session-${Date.now()}`;
    this.currentSessionId = sessionId;
    this.currentSessionName = '';
    this.chatMessages = [];
    this.initializeDefaultMessage();
    this.showSessions = false;
  }

  startNewSessionFromDropdown(): void {
    this.startNewSession();
  }

  loadSessions(): void {
    const stored = localStorage.getItem('chatSessions');
    if (stored) {
      this.chatSessions = JSON.parse(stored);
    }
  }

  saveSessions(): void {
    localStorage.setItem('chatSessions', JSON.stringify(this.chatSessions));
  }

  loadSession(session: ChatSession): void {
    this.currentSessionId = session.id;
    this.currentSessionName = session.title;
    const stored = localStorage.getItem(`chatSession-${session.id}`);
    this.chatMessages = stored ? JSON.parse(stored) : [];
    this.showSessions = false;
  }

  deleteSession(session: ChatSession, event: Event): void {
    event.stopPropagation();
    this.chatSessions = this.chatSessions.filter(s => s.id !== session.id);
    localStorage.removeItem(`chatSession-${session.id}`);
    this.saveSessions();

    if (this.currentSessionId === session.id) {
      this.startNewSession();
    }
  }

  toggleSessions(): void {
    this.showSessions = !this.showSessions;
  }
}
